import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill, { Quill } from "react-quill";
import ResizeModule from "@botom/quill-resize-module";
import "react-quill/dist/quill.snow.css";
import { newAuthService } from "../Service/Axios";
import { GLOBAL_FILE_UPLOAD_URL } from "../Constant/api";

// Add sizes to whitelist and register them
const Size = Quill.import("formats/size");
Size.whitelist = ["extra-small", "small", "medium", "large"];
Quill.register(Size, true);

// Add resize to whitelist and register them
Quill.register("modules/resize", ResizeModule);

export const RichTextEditor = ({
  value,
  variables,
  onChange,
  onUpload,
  readOnly,
}) => {
  const { t } = useTranslation();

  const quillRef = useRef(null);
  // const modules = {
  //     toolbar: [
  //         [{ header: [1, 2, false] }],
  //         ["bold", "italic", "underline", "strike", "blockquote"],
  //         [
  //             { list: "ordered" },
  //             { list: "bullet" },
  //             { indent: "-1" },
  //             { indent: "+1" },
  //         ],
  //         ["link"],
  //         ["clean"],
  //         ['image']

  //     ],
  // };

  const imageHandler = (e) => {
    const editor = quillRef.current.getEditor();
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (/^image\//.test(file.type)) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("appType", "reactQuillDemo");
        const response = await newAuthService(
          GLOBAL_FILE_UPLOAD_URL,
          "POST",
          formData
        );
        if (response.status) {
          editor.insertEmbed(editor.getSelection(), "image", response.data.url);
        }
      } else {
        toast.error("You could only upload images.");
      }
    };
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: "#toolbar",
        handlers: {
          image: imageHandler,
        },
      },
      resize: {
        locale: {
          // change them depending on your language
          altTip: "Hold down the alt key to zoom",
          floatLeft: "Left",
          floatRight: "Right",
          center: "Center",
          restore: "Restore",
        },
        toolbar: {},
      },
    }),
    []
  );

  const insertVariableText = (v) => {
    const variableText = `{${v}}`;
    const quillEditor = quillRef.current.getEditor();
    const selection = quillEditor.getSelection();

    if (selection) {
      quillEditor.insertText(selection.index, variableText);
    } else {
      quillEditor.insertText(quillEditor.getLength() - 1, variableText);
    }
  };

  // const CustomToolbar = () => (
  //     <div id="toolbar">
  //         <span className="ql-formats">
  //             <select className="ql-size">
  //                 <option value="extra-small">Normal</option>
  //                 <option value="small">Heading 2</option>
  //                 <option value="medium" selected>
  //                     Heading 3
  //                 </option>
  //                 <option value="large">Heading 4</option>
  //             </select>
  //         </span>
  //         <span className="ql-formats">
  //             <button className="ql-bold" />
  //             <button className="ql-italic" />
  //             <button className="ql-underline" />
  //             <button className="ql-strike" />
  //             <button className="ql-blockquote" />
  //         </span>
  //         <span className="ql-formats">
  //             <button className="ql-list" value="ordered" />
  //             <button className="ql-list" value="bullet" />
  //             <button className="ql-indent" value="-1" />
  //             <button className="ql-indent" value="+1" />
  //         </span>
  //         <span className="ql-formats">
  //             <button className="ql-image" />
  //             <button className="ql-file">
  //                 <input type="file" onChange={onUpload} />
  //             </button>
  //         </span>
  //     </div>
  // );

  return (
    <>
      <div className={`rich-text-editor ${readOnly ? "read-only" : ""}`}>
        <ReactQuill
          ref={quillRef}
          value={value}
          onChange={onChange}
          placeholder={""}
          modules={modules}
          formats={[
            "size",
            "font",
            "bold",
            "italic",
            "underline",
            "strike",
            "blockquote",
            "list",
            "bullet",
            "indent",
            "direction",
            "align",
            "color",
            "background",
            "link",
            "image",
            "video",
            "formula",
            "code-block",
            "clean",
          ]}
          readOnly={readOnly}
        />
        <div id="toolbar" style={{ display: readOnly ? "none" : "block" }}>
          <span className="ql-formats">
            <select className="ql-font"></select>
            <select className="ql-size">
              <option value="extra-small">{t("common.label.normal")}</option>
              <option value="small">{t("common.label.heading2")}</option>
              <option value="medium">{t("common.label.heading3")}</option>
              <option value="large">{t("common.label.heading4")}</option>
            </select>
          </span>

          <span className="ql-formats">
            <button className="ql-bold" />
            <button className="ql-italic" />
            <button className="ql-underline" />
            <button className="ql-strike" />
            <button className="ql-blockquote" />
          </span>

          <span className="ql-formats">
            <button className="ql-list" value="ordered" />
            <button className="ql-list" value="bullet" />
            <button className="ql-indent" value="-1" />
            <button className="ql-indent" value="+1" />
          </span>

          <span className="ql-formats">
            <button className="ql-script" value="super" />
            <button className="ql-script" value="sub" />
            <button className="ql-blockquote" />
            <button className="ql-direction" value="rtl"></button>
          </span>

          <span className="ql-formats">
            <select className="ql-align" />
            <select className="ql-color" />
            <select className="ql-background" />
          </span>

          <span className="ql-formats">
            <button className="ql-link" />
            <button className="ql-image" />
            <button className="ql-video" />
            <div className="ql-file upload-button">
              <input
                type="file"
                onChange={onUpload}
                className="upload-button__input"
              />
              <div className="upload-button__icon">
                <i className="bx bx-paperclip"></i>
              </div>
              {/* <label for="file"><i className="bx bx-user-circle"></i></label> */}
            </div>
          </span>

          <span className="ql-formats">
            <button className="ql-formula" />
            <button className="ql-code-block" />
            <button className="ql-clean" />
          </span>

          <span className="ql-formats">
            {variables && variables.length > 0 && (
              <div className="ql-variable user-email-dropdown">
                <button
                  type="button"
                  className="dropdown-toggle"
                  data-bs-toggle="dropdown"
                  aria-expanded="true"
                  data-bs-auto-close="outside"
                >
                  <i className="bx bx-list-minus"></i>
                </button>
                <ul className="dropdown-menu">
                  {variables.map((v) => (
                    <li key={v}>
                      <button
                        type="button"
                        onClick={() => insertVariableText(v)}
                      >
                        {v}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </span>
        </div>
      </div>
    </>
  );
};
